import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog'
import { AddressSearch, generateRawAddress, type AddressData } from './AddressSearch'
import { AppIcon } from './ui/icon'
import { mdiMapMarker } from '@mdi/js'

interface AddressPropertyEditorProps {
  value: Record<string, any> | null
  addressRaw: string | null
  geolocation: { lat: number; lng: number } | null
  readonly?: boolean
  onSave: (data: AddressData) => Promise<void>
}

export function AddressPropertyEditor({
  value,
  addressRaw,
  geolocation,
  readonly = false,
  onSave,
}: AddressPropertyEditorProps) {
  const { t } = useTranslation('properties')
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pendingData, setPendingData] = useState<AddressData | null>(null)

  const displayValue = value ? generateRawAddress(value) : null

  const handleSave = useCallback(async () => {
    if (!pendingData) return
    setSaving(true)
    try {
      await onSave(pendingData)
      setOpen(false)
    } finally {
      setSaving(false)
    }
  }, [pendingData, onSave])

  return (
    <div className="inline-flex items-center gap-2">
      <Button
        variant="ghost"
        size="sm"
        className="font-mono text-xs bg-muted/50 px-2 py-1 h-auto hover:bg-muted/70 transition-colors text-muted-foreground hover:text-foreground p-0 max-w-[300px] truncate text-left"
        data-testid="address-editor-dialog"
        onClick={() => { if (!readonly) setOpen(true) }}
      >
        {displayValue ? (
          <span className="flex items-center gap-1">
            <AppIcon path={mdiMapMarker} size="xs" />
            {displayValue}
          </span>
        ) : (
          '{...}'
        )}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="address-editor-dialog" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{t('address_editor.title')}</DialogTitle>
          </DialogHeader>
          <AddressSearch
            initialAddress={value}
            initialRaw={addressRaw || undefined}
            initialGeolocation={geolocation || undefined}
            autoSearch={!geolocation}
            onChange={setPendingData}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="address-cancel-btn">
              {t('address_editor.cancel')}
            </Button>
            {!readonly && (
              <Button onClick={handleSave} disabled={saving} data-testid="address-save-btn">
                {t('address_editor.save')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
